(ns iris.components.swipe
  (:require
   [reagent.core :as reagent]))

(def default-options
  {:as :div
   :direction :left
   :enabled? true
   :ignore-selector nil
   :min-distance 60})

(def component-option-keys
  #{:as
    :direction
    :enabled?
    :ignore-selector
    :min-distance
    :on-swipe
    :ref})

(defn swipe-distance
  [start end]
  {:dx (- (:x end) (:x start))
   :dy (- (:y end) (:y start))})

(defn qualifies-swipe?
  [{:keys [direction min-distance]
    :or {direction :left
         min-distance 60}}
   start
   end]
  (if (and (number? (:x start))
           (number? (:y start))
           (number? (:x end))
           (number? (:y end)))
    (let [{:keys [dx dy]} (swipe-distance start end)
          abs-dx (js/Math.abs dx)
          abs-dy (js/Math.abs dy)]
      (boolean
       (and (>= abs-dx min-distance)
            (> abs-dx abs-dy)
            (case direction
              :left (neg? dx)
              :right (pos? dx)
              false))))
    false))

(defn- touch-at
  [touch-list idx]
  (when (and touch-list (< idx (.-length touch-list)))
    (.item touch-list idx)))

(defn- touch-point
  [touch]
  (when touch
    {:x (.-clientX touch)
     :y (.-clientY touch)}))

(defn- single-touch-point
  [touch-list]
  (when (and touch-list (= 1 (.-length touch-list)))
    (touch-point (touch-at touch-list 0))))

(defn- start-point
  [e]
  (single-touch-point (.-touches e)))

(defn- end-point
  [e]
  (single-touch-point (.-changedTouches e)))

(defn- ignored-target?
  [e selector]
  (and selector
       (.-target e)
       (.closest (.-target e) selector)))

(defn- dom-attrs
  [opts node]
  (let [user-ref (:ref opts)]
    (assoc (apply dissoc opts component-option-keys)
           :ref (fn [el]
                  (reset! node el)
                  (cond
                    (fn? user-ref) (user-ref el)
                    (satisfies? IReset user-ref) (reset! user-ref el))))))

(defn swipeable
  "Reusable touch swipe detector. Calls `:on-swipe` once on touchend when the
  configured direction qualifies. Movement does not write Reagent/re-frame state."
  [& _]
  (let [node (atom nil)
        start (atom nil)
        latest-opts (atom default-options)]
    (letfn [(enabled? []
              (true? (:enabled? @latest-opts)))
            (on-touch-start [e]
              (if (and (enabled?)
                       (not (ignored-target? e (:ignore-selector @latest-opts))))
                (reset! start (start-point e))
                (reset! start nil)))
            (on-touch-end [e]
              (when-let [start-point @start]
                (when-let [end-point (end-point e)]
                  (when (qualifies-swipe? @latest-opts start-point end-point)
                    (when-let [on-swipe (:on-swipe @latest-opts)]
                      (on-swipe {:direction (:direction @latest-opts)
                                 :start start-point
                                 :end end-point
                                 :event e})))))
              (reset! start nil))
            (on-touch-cancel [_]
              (reset! start nil))
            (attach! [el]
              (when el
                (.addEventListener el "touchstart" on-touch-start #js {:passive true})
                (.addEventListener el "touchend" on-touch-end #js {:passive true})
                (.addEventListener el "touchcancel" on-touch-cancel #js {:passive true})))
            (detach! [el]
              (when el
                (.removeEventListener el "touchstart" on-touch-start false)
                (.removeEventListener el "touchend" on-touch-end false)
                (.removeEventListener el "touchcancel" on-touch-cancel false)))]
      (reagent/create-class
       {:display-name "swipeable"
        :component-did-mount
        (fn [_]
          (attach! @node))
        :component-will-unmount
        (fn [_]
          (detach! @node))
        :reagent-render
        (fn [opts & children]
          (let [opts (merge default-options opts)
                tag (:as opts)]
            (reset! latest-opts opts)
            (into [tag (dom-attrs opts node)] children)))}))))
